import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import FeatureFlagsService from '../services/FeatureFlagsService';

/**
 * Initial state for the feature flags slice
 */
const initialState = {
  flags: {},
  merchantFlags: {},
  loading: false,
  error: null,
};

/**
 * Thunk to fetch global feature flags
 */
export const fetchFeatureFlags = createAsyncThunk(
  'featureFlags/fetchFeatureFlags',
  async (_, { rejectWithValue }) => {
    try {
      const flags = await FeatureFlagsService.fetchFeatureFlags();
      return flags;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk to fetch merchant-specific feature flags
 */
export const fetchMerchantFeatureFlags = createAsyncThunk(
  'featureFlags/fetchMerchantFeatureFlags',
  async (merchantId, { rejectWithValue }) => {
    try {
      const flags = await FeatureFlagsService.fetchMerchantFeatureFlags(merchantId);
      return { merchantId, flags };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Redux slice for managing feature flags
 */
const featureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {
    resetFeatureFlags: (state) => {
      state.flags = {};
      state.merchantFlags = {};
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFeatureFlags
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        state.flags = action.payload;
        state.loading = false;
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchMerchantFeatureFlags
      .addCase(fetchMerchantFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMerchantFeatureFlags.fulfilled, (state, action) => {
        const { merchantId, flags } = action.payload;
        state.merchantFlags[merchantId] = flags;
        state.loading = false;
      })
      .addCase(fetchMerchantFeatureFlags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetFeatureFlags } = featureFlagsSlice.actions;

// Selectors
const getFeatureFlags = (state) => state.featureFlags;
export const getAllFeatureFlags = createSelector(getFeatureFlags, (featureFlags) => featureFlags.flags);
export const getMerchantFeatureFlags = createSelector(
  getFeatureFlags,
  (_, merchantId) => merchantId,
  (featureFlags, merchantId) => featureFlags.merchantFlags[merchantId] || {}
);

/**
 * Checks if a specific feature flag is enabled
 * @param {Object} state - Redux state
 * @param {string} flagName - Feature flag name
 * @param {string} [merchantId] - Merchant ID (optional)
 * @returns {boolean} - true if the feature flag is enabled, false otherwise
 */
export const isFeatureFlagEnabled = (state, flagName, merchantId) => {
  // If a merchantId is provided, first check in the merchant-specific flags
  if (merchantId && state.featureFlags.merchantFlags[merchantId]) {
    const merchantFlag = state.featureFlags.merchantFlags[merchantId][flagName];
    if (merchantFlag !== undefined) {
      return merchantFlag;
    }
  }
  
  // If there are no merchant-specific flags or the flag is not defined for the merchant,
  // check in the global flags
  return state.featureFlags.flags[flagName] || false;
};

export default featureFlagsSlice.reducer;
